import { useUrlRow } from '../hooks/use-url-state.js';
import type { DeploymentAnalysis, DeploymentPlan, DeploymentSummary } from '../api/types.js';
import { RichText } from './rich-text.js';
import { Link } from 'react-router-dom';
import { Modal } from './modal.js';
import { useState } from 'react';
import { useDecideDeployment, useDeployment, useDeployments, useStartAnalysis } from '../hooks/use-deployments.js';
import { useToast } from '../hooks/use-toast.js';
import { Button } from './button.js';
import { timeAgo } from '../utils/time-ago.js';
import { SetAsideControl } from './set-aside-form.js';

// Deployment approvals of a server (ADR-022): the facts aidev read from GitHub for each deployment waiting
// on the operator. Read-only: approving and rejecting happen in GitHub.

const short = (sha?: string) => (sha ? sha.slice(0, 7) : '?');

type Severity = 'danger' | 'caution' | 'clear' | 'unknown';

function severity(d: DeploymentSummary): Severity {
  if (d.flags?.prodDestroy || d.flags?.noDestroyHit) return 'danger';
  if (d.flags?.superseded || d.flags?.planUnreadable || (d.facts?.failedJobs.length ?? 0) > 0) return 'caution';
  return d.facts ? 'clear' : 'unknown';
}

const EDGE: Record<Severity, string> = {
  danger: 'var(--color-error)',
  caution: 'var(--color-warn)',
  clear: 'var(--color-success)',
  unknown: 'var(--color-border-2)',
};

/** The plan as OpenTofu counts it; destroy carries the weight, in red on production */
function PlanCounts({ plan, prod }: { plan?: DeploymentPlan; prod: boolean }) {
  if (!plan || plan.status === 'not-found') return null;
  if (plan.status === 'no-changes') return <span className="text-xs" style={{ color: 'var(--color-text-3)' }}>No changes</span>;
  const destroy = plan.destroy ?? 0;
  return (
    <span className="flex items-baseline gap-2 text-sm tabular-nums" style={{ fontFamily: 'var(--font-mono)' }}
      aria-label={`Plan: ${plan.add ?? 0} to add, ${plan.change ?? 0} to change, ${destroy} to destroy`}>
      <span style={{ color: 'var(--color-success)' }}>+{plan.add ?? 0}</span>
      <span style={{ color: 'var(--color-warn)' }}>~{plan.change ?? 0}</span>
      <span className={destroy > 0 ? 'rounded px-1 font-semibold' : ''}
        style={destroy > 0
          ? { color: 'var(--color-error)', backgroundColor: 'var(--color-error-dim)', ...(prod ? { boxShadow: 'inset 0 0 0 1px var(--color-error)' } : {}) }
          : { color: 'var(--color-text-3)' }}>
        −{destroy}
      </span>
    </span>
  );
}

function Warnings({ d }: { d: DeploymentSummary }) {
  const f = d.facts;
  const lines: Array<{ tone: 'error' | 'warn'; text: string }> = [];
  if (d.flags?.superseded) {
    lines.push({ tone: 'warn', text: `${d.headBranch} has moved on to ${short(f?.branchHead)} since this run started at ${short(d.headSha)}. Approving would apply nothing: start a new run on the current head.` });
  }
  if (d.flags?.noDestroyHit) lines.push({ tone: 'error', text: `Nothing may be destroyed in ${d.repo}, and this plan destroys ${f?.plan.destroy ?? 'some'} resources.` });
  else if (d.flags?.prodDestroy) lines.push({ tone: 'error', text: `This plan destroys ${f?.plan.destroy ?? 'some'} resources in ${d.environment}. Open the list before approving.` });
  if (f?.plan.mismatch) lines.push({ tone: 'warn', text: 'The plan line disagrees with the resources listed in the log. Read the plan in GitHub.' });
  else if (d.flags?.planUnreadable) lines.push({ tone: 'warn', text: `No plan was found in the log of ${f?.plan.job ?? 'the plan job'}. Read the plan in GitHub before approving.` });
  if (f?.plan.truncated) lines.push({ tone: 'warn', text: 'The plan log was too long to read in full; the counts may be incomplete.' });
  if (f?.failedJobs.length) lines.push({ tone: 'warn', text: `Failed in this run: ${f.failedJobs.join(', ')}.` });
  if (!lines.length) return null;
  return (
    <ul className="space-y-1 px-4 pb-3 text-sm">
      {lines.map((l) => (
        <li key={l.text} style={{ color: l.tone === 'error' ? 'var(--color-error)' : 'var(--color-warn)' }}>{l.text}</li>
      ))}
    </ul>
  );
}

function AddressList({ title, addresses, total }: { title: string; addresses: string[]; total?: number }) {
  if (!addresses.length) return null;
  return (
    <div>
      <h4 className="text-xs font-semibold" style={{ color: 'var(--color-text-2)' }}>
        {title} ({total && total > addresses.length ? `${addresses.length} of ${total}` : addresses.length})
      </h4>
      <ul className="mt-1 max-h-60 overflow-y-auto rounded border p-2 text-xs" style={{ borderColor: 'var(--color-border)', fontFamily: 'var(--font-mono)', color: 'var(--color-text-2)' }}>
        {addresses.map((a) => <li key={a} className="break-all">{a}</li>)}
      </ul>
    </div>
  );
}

function DeploymentDetailPanel({ serverId, id, summary, isAdmin }: { serverId: string; id: string; summary: DeploymentSummary; isAdmin: boolean }) {
  const { data, isLoading, isError, error } = useDeployment(serverId, id);
  if (isLoading) return <p className="px-4 pb-4 text-sm" style={{ color: 'var(--color-text-3)' }}>Loading the facts…</p>;
  if (isError || !data) return <p className="px-4 pb-4 text-sm" style={{ color: 'var(--color-error)' }}>Could not load this deployment: {(error as Error | null)?.message ?? 'not found'}.</p>;
  const f = data.facts;
  const pinned = f?.workflows.filter((w) => w.pinned) ?? [];
  return (
    <div className="space-y-3 border-t px-4 py-3" style={{ borderColor: 'var(--color-border)' }}>
      {data.analysis && <AnalysisSection analysis={data.analysis} />}
      {f && (
        <>
          <AddressList title="Destroyed" addresses={f.plan.destroyed} />
          <AddressList title="Destroyed and recreated" addresses={f.plan.replaced} />
          {f.plan.status === 'changes' && !f.plan.destroyed.length && !f.plan.replaced.length && (
            <p className="text-sm" style={{ color: 'var(--color-text-2)' }}>Nothing is destroyed or recreated.</p>
          )}
          {pinned.length > 0 && (
            <p className="text-sm" style={{ color: 'var(--color-text-2)' }}>
              This run uses a pinned pipeline version, which may lack later fixes: {pinned.map((w) => w.path.split('/').pop()).join(', ')}.
            </p>
          )}
          {f.plan.job && f.freshnessGuard === false && (
            <p className="text-sm" style={{ color: 'var(--color-warn)' }}>The pipeline of this run does not check that its commit is still current before applying.</p>
          )}
          {f.errors.length > 0 && (
            <p className="text-xs" style={{ color: 'var(--color-text-3)' }}>Not read from GitHub: {f.errors.join('; ')}.</p>
          )}
        </>
      )}
      <p className="text-xs" style={{ color: 'var(--color-text-3)' }}>
        Commit <code style={{ fontFamily: 'var(--font-mono)' }}>{short(data.headSha)}</code> on {data.headBranch}
        {data.requestor ? `, started by ${data.requestor}` : ''}. {f ? `Checked ${timeAgo(f.computedAt)}.` : 'Not checked yet.'}
      </p>
      {isAdmin && summary.state === 'waiting' && <DecisionBar serverId={serverId} d={summary} />}
    </div>
  );
}

function destroyCount(d: DeploymentSummary): number {
  return d.facts?.plan.destroy ?? 0;
}

/** The operator's decision, posted on GitHub under their name; destroys must be typed back to approve */
function DecisionDialog({ serverId, d, state, onClose }: { serverId: string; d: DeploymentSummary; state: 'approved' | 'rejected'; onClose: () => void }) {
  const decide = useDecideDeployment(serverId);
  const { toast } = useToast();
  const [comment, setComment] = useState('');
  const [typed, setTyped] = useState('');
  const destroys = destroyCount(d);
  const needsCount = state === 'approved' && destroys > 0;
  const approving = state === 'approved';
  const blocked = needsCount && typed.trim() !== String(destroys);
  const title = `${approving ? 'Approve' : 'Reject'} the deployment of ${d.repo} to ${d.environment}`;
  function submit() {
    decide.mutate({ id: d.id, state, ...(comment.trim() ? { comment: comment.trim() } : {}), ...(needsCount ? { confirmDestroy: destroys } : {}) }, {
      onSuccess: () => { toast(approving ? 'Deployment approved on GitHub' : 'Deployment rejected on GitHub', 'success'); onClose(); },
    });
  }
  return (
    <Modal title={title} onClose={onClose}>
        <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text)' }}>{title}</h3>
        <p className="mt-1 text-sm" style={{ color: 'var(--color-text-2)' }}>
          {d.org}/{d.repo}, {d.workflowName} at {d.headSha.slice(0, 7)}. GitHub records the decision under your name.
        </p>
        {d.analysis && (
          <p className="mt-3 text-sm" style={{ color: 'var(--color-text-2)' }}>
            The analysis recommends <RecommendationChip analysis={d.analysis} />{d.analysis.reason ? ` ${d.analysis.reason}` : ''}
          </p>
        )}
        {needsCount && (
          <div className="mt-4 rounded-md p-3" style={{ backgroundColor: 'var(--color-error-dim)' }}>
            <label htmlFor={`confirm-${d.id}`} className="text-sm font-medium" style={{ color: 'var(--color-error)' }}>
              This plan destroys or recreates {destroys} resources. Type {destroys} to confirm.
            </label>
            <input id={`confirm-${d.id}`} data-autofocus inputMode="numeric" value={typed} onChange={(e) => setTyped(e.target.value)}
              className="form-input mt-2 block w-28 rounded-md border px-3 py-1.5 text-sm"
              style={{ borderColor: 'var(--color-error)', backgroundColor: 'var(--color-surface)', color: 'var(--color-text)' }} />
          </div>
        )}
        <label htmlFor={`comment-${d.id}`} className="mt-4 block text-sm font-medium" style={{ color: 'var(--color-text-2)' }}>Comment on GitHub (optional)</label>
        <textarea id={`comment-${d.id}`} {...(needsCount ? {} : { 'data-autofocus': true })} value={comment} onChange={(e) => setComment(e.target.value)} rows={3} maxLength={1000}
          className="form-input mt-1 block w-full rounded-md border px-3 py-2 text-sm"
          style={{ borderColor: 'var(--color-border-2)', backgroundColor: 'var(--color-surface)', color: 'var(--color-text)' }} />
        {decide.isError && (
          <p role="alert" className="mt-3 text-sm" style={{ color: 'var(--color-error)' }}>{(decide.error as Error).message}</p>
        )}
        <div className="mt-6 flex justify-end gap-3">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant={approving ? 'primary' : 'danger'} disabled={blocked || decide.isPending} onClick={submit}>
            {decide.isPending ? 'Sending…' : approving ? 'Approve on GitHub' : 'Reject on GitHub'}
          </Button>
        </div>
    </Modal>
  );
}

/**
 * Where the decision is taken: at the end of the detail, after the facts and the analysis. A superseded run
 * cannot be approved, so it offers a sentence and a way to clear it instead of a disabled button.
 */
function DecisionBar({ serverId, d }: { serverId: string; d: DeploymentSummary }) {
  const [deciding, setDeciding] = useState<'approved' | 'rejected' | null>(null);
  const start = useStartAnalysis(serverId);
  const { toast } = useToast();
  const superseded = !!d.flags?.superseded;
  const sessionName = `Deploy ${d.org}/${d.repo} to ${d.environment}`;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-3" style={{ borderColor: 'var(--color-border)' }}>
      <div className="flex flex-wrap items-center gap-3">
        {superseded ? (
          <>
            <span className="text-sm" style={{ color: 'var(--color-warn)' }}>Behind {d.headBranch}: start a new run in GitHub rather than approving this one.</span>
            <Button size="sm" variant="ghost" onClick={() => setDeciding('rejected')}>Reject this run</Button>
          </>
        ) : (
          <>
            <Button size="sm" variant="primary" onClick={() => setDeciding('approved')}>Approve</Button>
            <Button size="sm" variant="ghost" onClick={() => setDeciding('rejected')}>Reject</Button>
            {/* ADR-039: needs more first; it stays pending in GitHub, nothing deployed, nothing rejected */}
            <SetAsideControl serverId={serverId} itemId={`deploy:${d.id}`} />
          </>
        )}
      </div>
      <span className="text-xs" style={{ color: 'var(--color-text-3)' }}>
        {d.analysisRequestedAt ? <>Analysis started {timeAgo(d.analysisRequestedAt)}. </> : null}
        {d.analysisRequestedAt && (
          <Link to={`/servers/${serverId}?q=${encodeURIComponent(sessionName)}`} className="hover:underline" style={{ color: 'var(--color-brand-light)' }}>Open the session</Link>
        )}
        {d.analysisRequestedAt && ' or '}
        <button type="button" disabled={start.isPending} className="hover:underline disabled:opacity-50" style={{ color: 'var(--color-brand-light)' }}
          onClick={() => start.mutate(d.id, {
            onSuccess: () => toast('Analysis started', 'success'),
            onError: (err) => toast(`Could not start the analysis: ${(err as Error).message}`, 'error'),
          })}>
          {start.isPending ? 'starting…' : d.analysisRequestedAt ? 'analyse again' : 'Analyse in a session'}
        </button>
      </span>
      {deciding && <DecisionDialog serverId={serverId} d={d} state={deciding} onClose={() => setDeciding(null)} />}
    </div>
  );
}

function DeploymentRow({ serverId, d, isAdmin }: { serverId: string; d: DeploymentSummary; isAdmin: boolean }) {
  const { open, toggle, ref } = useUrlRow<HTMLLIElement>('deploy', d.id);
  const panelId = `deployment-${d.id.replace(/[^a-z0-9]/gi, '-')}`;
  const others = d.facts?.otherReviewers ?? [];
  return (
    <li ref={ref} id={`deploy-row-${d.id}`} className="card overflow-hidden" style={{ borderLeft: `3px solid ${EDGE[severity(d)]}` }}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3">
        <button type="button" aria-expanded={open} aria-controls={panelId} onClick={toggle}
          className="flex min-w-0 flex-1 basis-full items-center gap-3 rounded text-left focus-visible:outline focus-visible:outline-2 sm:basis-auto">
          <svg className="h-3.5 w-3.5 flex-shrink-0 transition-transform motion-reduce:transition-none" style={{ color: 'var(--color-text-3)', transform: open ? 'rotate(90deg)' : 'none' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
          </svg>
          <span className="min-w-0 truncate text-sm">
            <span style={{ color: 'var(--color-text-3)' }}>{d.org}/</span>
            <span style={{ color: 'var(--color-text)' }}>{d.repo}</span>
            <span style={{ color: 'var(--color-text-2)' }}> to {d.environment}</span>
          </span>
          <span className="hidden truncate text-xs sm:inline" style={{ color: 'var(--color-text-3)' }}>{d.workflowName}</span>
        </button>
        <PlanCounts plan={d.facts?.plan} prod={!!(d.flags?.prodDestroy || d.flags?.noDestroyHit)} />
        {d.analysis && <RecommendationChip analysis={d.analysis} />}
        {others.length > 0 && (
          <span className="text-xs" style={{ color: 'var(--color-text-3)' }}>{others.join(' or ')} can approve instead</span>
        )}
        <span className="flex-shrink-0 text-xs" style={{ color: 'var(--color-text-3)' }}>{timeAgo(d.requestedAt)}</span>
        <a href={d.runUrl} target="_blank" rel="noreferrer" className="flex-shrink-0 text-xs hover:underline" style={{ color: 'var(--color-brand-light)' }}>
          Review in GitHub
        </a>
      </div>
      <Warnings d={d} />
      {open && <div id={panelId}><DeploymentDetailPanel serverId={serverId} id={d.id} summary={d} isAdmin={isAdmin} /></div>}
    </li>
  );
}

const RECOMMENDATION: Record<DeploymentAnalysis['effective'], { label: string; color: string; dim: string }> = {
  approve: { label: 'Approve', color: 'var(--color-success)', dim: 'var(--color-success-dim)' },
  approve_with_destroy: { label: 'Approve, with destroys', color: 'var(--color-error)', dim: 'var(--color-error-dim)' },
  wait: { label: 'Set aside', color: 'var(--color-warn)', dim: 'var(--color-warn-dim)' },
  rerun: { label: 'Re-run on the head', color: 'var(--color-warn)', dim: 'var(--color-warn-dim)' },
  reject: { label: 'Reject', color: 'var(--color-error)', dim: 'var(--color-error-dim)' },
};

function RecommendationChip({ analysis }: { analysis: DeploymentAnalysis }) {
  const r = RECOMMENDATION[analysis.effective];
  return (
    <span className="flex-shrink-0 whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-medium" style={{ backgroundColor: r.dim, color: r.color }}>
      {r.label}
    </span>
  );
}

function AnalysisSection({ analysis }: { analysis: DeploymentAnalysis }) {
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h4 className="text-xs font-semibold" style={{ color: 'var(--color-text-2)' }}>Analysis</h4>
        <RecommendationChip analysis={analysis} />
        <span className="text-xs" style={{ color: 'var(--color-text-3)' }}>{timeAgo(analysis.collectedAt)}</span>
      </div>
      {analysis.floor && (
        <p className="text-sm" style={{ color: 'var(--color-warn)' }}>
          The session recommended "{RECOMMENDATION[analysis.recommendation].label}"; aidev shows "{RECOMMENDATION[analysis.effective].label}" because {analysis.floor}.
        </p>
      )}
      {analysis.reason && <p className="text-sm" style={{ color: 'var(--color-text)' }}>{analysis.reason}</p>}
      <RichText text={analysis.summaryFr} />
      {analysis.concerns.length > 0 && (
        <ul className="space-y-1 text-sm">
          {analysis.concerns.map((c, i) => (
            <li key={i} className="flex gap-2">
              <span className="flex-shrink-0 rounded px-1.5 text-[11px]" style={{ backgroundColor: 'var(--color-surface-3)', color: 'var(--color-text-2)' }}>{c.kind}</span>
              <span style={{ color: 'var(--color-text)' }}>{c.detail}</span>
            </li>
          ))}
        </ul>
      )}
      {analysis.notChecked.length > 0 && (
        <div>
          <h5 className="text-xs font-semibold" style={{ color: 'var(--color-text-2)' }}>Not checked</h5>
          <ul className="mt-1 list-disc pl-5 text-sm" style={{ color: 'var(--color-text-2)' }}>{analysis.notChecked.map((n, i) => <li key={i}>{n}</li>)}</ul>
        </div>
      )}
      {analysis.questions.length > 0 && (
        <div>
          <h5 className="text-xs font-semibold" style={{ color: 'var(--color-text-2)' }}>Questions</h5>
          <ul className="mt-1 list-disc pl-5 text-sm" style={{ color: 'var(--color-text)' }}>{analysis.questions.map((q, i) => <li key={i}>{q}</li>)}</ul>
        </div>
      )}
    </div>
  );
}

const DECISION = { approved: 'Approved', rejected: 'Rejected', completed: 'Run finished' } as const;

export function DeploymentApprovals({ serverId, isAdmin }: { serverId: string; isAdmin: boolean }) {
  const { data, error } = useDeployments(serverId);
  const list = data ?? [];
  const loadError = error as Error | null | undefined;
  // Servers that do not prepare deployments get no empty section
  if (!list.length && !loadError) return null;
  const waiting = list.filter((d) => d.state === 'waiting');
  const decided = list.filter((d) => d.state !== 'waiting').slice(0, 10);
  return (
    <section aria-labelledby="deployments-title" className="mb-8 min-w-0">
      <h2 id="deployments-title" className="text-lg font-semibold" style={{ color: 'var(--color-text)' }}>Deployment approvals</h2>
      <p className="mt-0.5 text-sm" style={{ color: 'var(--color-text-3)' }}>
        What aidev read in GitHub for the deployments waiting on you. Approve or reject them in GitHub.
      </p>
      {loadError && (
        <p role="alert" className="mt-3 text-sm" style={{ color: 'var(--color-error)' }}>
          Could not load the deployment approvals: {loadError.message}. They refresh every minute.
        </p>
      )}
      {waiting.length > 0 ? (
        <ul className="mt-4 space-y-2">
          {waiting.map((d) => <DeploymentRow key={d.id} serverId={serverId} d={d} isAdmin={isAdmin} />)}
        </ul>
      ) : !loadError && (
        <p className="mt-4 text-sm" style={{ color: 'var(--color-text-2)' }}>No deployment waits on you.</p>
      )}
      {decided.length > 0 && (
        <details className="mt-4 text-sm">
          <summary className="cursor-pointer text-xs font-semibold" style={{ color: 'var(--color-text-2)' }}>Recent decisions ({decided.length})</summary>
          <ul className="mt-2 space-y-1 text-xs" style={{ color: 'var(--color-text-3)' }}>
            {decided.map((d) => (
              <li key={d.id}>
                <a href={d.runUrl} target="_blank" rel="noreferrer" className="hover:underline" style={{ color: 'var(--color-text-2)' }}>{d.repo} to {d.environment}</a>
                {': '}{DECISION[d.state as keyof typeof DECISION]}{d.decidedBy ? ` by ${d.decidedBy}` : ''} {timeAgo(d.decidedAt ?? d.requestedAt)}
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
